import { useEffect, useRef } from "react";
import { Analytics } from "@/lib/analytics";

const THRESHOLDS = [25, 50, 75, 100];

/**
 * Hook to track scroll depth on a blog article page.
 * Sends Analytics.blogScrollDepth() events at 25%, 50%, 75%, and 100%.
 * 
 * @param slug - The article slug for analytics tracking
 */
export const useBlogScrollDepth = (slug: string | undefined) => {
  const trackedThresholds = useRef<Set<number>>(new Set());
  const currentSlug = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Reset tracked thresholds when slug changes
    if (currentSlug.current !== slug) {
      trackedThresholds.current.clear();
      currentSlug.current = slug;
    }

    if (!slug) return;

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      // Avoid division by zero on short pages
      if (scrollHeight <= 0) return;

      const scrollPosition = window.scrollY;
      const scrollPercentage = Math.round((scrollPosition / scrollHeight) * 100);

      THRESHOLDS.forEach((threshold) => {
        if (scrollPercentage >= threshold && !trackedThresholds.current.has(threshold)) {
          trackedThresholds.current.add(threshold);
          Analytics.blogScrollDepth(slug, threshold);
        }
      });
    };

    // Add scroll listener with passive flag for performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Check initial scroll position
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [slug]);
};
